import { useEffect, useRef, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { skip } from 'rxjs/operators';
import { Categories, empty } from './core';
import { useCreate } from './use-create';
import { useInitialize } from './use-initialize';
import { useRemove } from './use-remove';
import { useUpdate } from './use-update';

type UseCategoriesResult = {
  categories: Categories;
  remove: ReturnType<typeof useRemove>;
  create: ReturnType<typeof useCreate>;
  update: ReturnType<typeof useUpdate>;
};

export const useCategories = (): UseCategoriesResult => {
  const categories$ = useRef(new BehaviorSubject<Categories>(empty()));
  const [categories, setCategories] = useState<Categories>(empty());
  const [initialized, setInitialized] = useState(false);
  const create = useCreate(categories$, initialized);
  const remove = useRemove(categories$, initialized);
  const update = useUpdate(categories$, initialized);

  useEffect(() => {
    const subscription = categories$.current
      .asObservable()
      .pipe(skip(1))
      .subscribe(setCategories);

    return () => subscription.unsubscribe();
  }, [categories$, setCategories]);

  useInitialize(categories$, initialized, setInitialized);

  return { categories, remove, create, update };
};
